<template>
  <section
    ref="$block"
    :class="{
      'osk-home-blocks-additional-collection': true,
    }"
  >
    <div class="osk-wrapper">
      <h2
        v-if="data.title"
        class="osk-home-blocks-additional-collection__title"
      >
        {{ data.title }}
      </h2>

      <UikitSwiper
        ref="$swiper"
        navigation
        off
        :breakpoints="sliderBreakpoints"
        :css-mode="!isDesktop"
        class="osk-home-blocks-additional-collection__swiper"
        @slide-change="handleSlideChange"
      >
        <UikitSwiperSlide
          v-for="(item, index) in items"
          :key="index"
        >
          <nuxt-link
            class="osk-home-blocks-additional-collection__card"
            :to="item.link"
            @click="handleClick(item, index)"
          >
            <img
              :src="item.imgPath"
              alt="main-image"
              class="osk-home-blocks-additional-collection__card-image"
            >

            <div class="osk-home-blocks-additional-collection__card-content">
              <span class="osk-home-blocks-additional-collection__card-title">
                {{ item.title }}
              </span>

              <div
                v-if="!isEqual(item)"
                class="osk-home-blocks-additional-collection__card-text"
              >
                {{ item.description }}
              </div>
            </div>
          </nuxt-link>
        </UikitSwiperSlide>
      </UikitSwiper>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { PrimaryContentDTO, AdditionalCollectionDTO } from '~/restAPI/data-contracts';

const props = defineProps<{
  data: PrimaryContentDTO
}>();

const $block = ref<ComponentPublicInstance>();
const $swiper = ref();

const { isDesktop } = useUiBreakpoints();

const sliderBreakpoints = {
  0: {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  478: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  580: {
    slidesPerView: 3.3,
    slidesPerGroup: 4,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  700: {
    slidesPerView: 4,
    slidesPerGroup: 4,
    spaceBetween: 0,
    navigation: { enabled: false },
  },
  1024: {
    slidesPerView: 4,
    slidesPerGroup: 4,
    spaceBetween: 24,
    navigation: { enabled: true },
  },
};

const items = computed(() => {
  const contentList = (props.data.contentList ?? []) as AdditionalCollectionDTO[];

  return contentList.map((item) => ({
    ...item,
    link: genSPALink(item.link),
  }));
});

const allIds = computed(() => items.value.map((i) => i.id!));

const { event, clickEvent } = useSwiperAnalytics(
  $swiper,
  $block,
  allIds,
  {
    chapter: props.data.contentType,
    chapter_index: props.data.chapterIndex,
    segment_id: props.data.segmentId,
    id_chapter: props.data.id,
  },
  sliderBreakpoints,
);

function handleClick(item: {id?: string}, index: number) {
  clickEvent({
    id_item: item.id,
    index,
    item: 'banner',
  });
}

function handleSlideChange() {
  event();
}

function isEqual(item: AdditionalCollectionDTO) {
  return item.description?.toLowerCase() === item.title?.toLowerCase();
}
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-home-blocks-additional-collection {
  padding-bottom: 42px;

  @include media-query(lg-and-up) {
    padding-bottom: 82px;
  }

  .osk-wrapper {
    padding: 0;

    @include media-query(lg-and-up) {
      padding: 0 16px;
    }
  }

  .swiper-slide {
    padding: 0 16px;
    margin-right: -20px;

    @include media-query(lg-and-up) {
      padding: 0;
      margin: 0;
    }
  }

  &__title {
    @include overflow-ellipsis;
    @include font-style($font-size-heading, normal, $font-weight-bold);
    padding: 0 16px 22px;

    @include media-query(lg-and-up) {
      text-transform: uppercase;
      text-align: center;
      padding: 0 80px 34px;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
  }

  &__card-image {
    display: block;
    width: 100%;
    height: 156px;
    object-fit: cover;
    margin-bottom: $indent-medium;
    border-radius: 2px;

    @include media-query(md-and-up) {
      margin-bottom: $indent-mmedium;
      border-radius: 4px;
      height: 232px;
    }

    @include media-query(lg-and-up) {
      height: 252px;
    }

    @include media-query(xl-and-up) {
      height: 282px;
    }
  }

  &__card-content {
    display: flex;
    flex-direction: column;
  }

  &__card-title {
    @include overflow-ellipsis;
    @include font-style($font-size-subheading, normal, $font-weight-bold);

    @include media-query(lg-and-up) {
      text-transform: uppercase;
    }
  }

  &__card-text {
    @include line-clamp;
    @include font-style($font-size-caption, normal, $font-weight-medium);
    margin-top: $indent-small;

    @include media-query(lg-and-up) {
      margin-top: $indent-compact;
      font-size: 14px;
    }
  }

  .osk-swiper_off {
    .swiper-wrapper {
      overflow: visible;
      justify-content: space-between;
    }

    .swiper-slide {
      max-width: 100%;

      @include media-query(md-and-up) {
        max-width: 25%;
        margin-right: 24px;
        flex-shrink: 0;
      }
    }
  }

  .osk-swiper-button-prev {
    top: -50px !important;
  }

  .osk-swiper-button-next {
    top: -50px !important;
  }

  &__swiper {
    .osk-swiper-button-prev,
    .osk-swiper-button-next {
      @media screen and (min-width: 700px) {
        top: -42px !important;
      }

      @include media-query(lg-and-up) {
        top: -58px !important;
      }
    }
  }
}
</style>
